import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { notification } from "antd";
import { change } from "redux-form";
import moment from "moment";

import { getConsequences } from "../../../../../actions/v2/SWMSConsequencesActions";
import { getLikelihoods } from "../../../../../actions/v2/SWMSLikelihoodActions";
import { Strings } from "../../../../../dataProvider/localize";
import { getCategoryFilters } from "../../../../../actions/v2/SwmsCategoryActions";
import { capitalizeFirst } from "../../../../../utils/common";
import AddEditJSA2ActivityForm from "./AddEditJSA2ActivityForm";

import "./JobSwmsFormStyle.css";

const dateFormatter = (v) => moment(v).format("DD/MM/YYYY hh:mm A");

const formatAction = (v) => {
  let res = "";
  res = v && v.length ? capitalizeFirst(v.split("_").join(" "), true, true) : ""
  return res;
}

export default function AddEditJSA2Activity({ onClose, selectedActivity, onActivitySubmit }) {

  const dispatch = useDispatch();

  const [initialValues, setInitialValues] = useState({ actions: [{ action_name: "", hazards: [{ hazard_name: "", consequences: [{}] }] }] });
  const [loadingData, setLoadingData] = useState(false);
  const [likelihoods, setLikelihoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [consequences, setConsequences] = useState([]);

  const controller = useRef();
  useEffect(() => {
    if (selectedActivity) {
      let activityValues = {
        id: selectedActivity.activity_id,
        activity_name: selectedActivity.activity_name,
        category: { label: selectedActivity.category_name, value: selectedActivity.category_id },
        method: { label: selectedActivity.method_name, value: selectedActivity.method_id },
        actions: selectedActivity.fm_swms_actions_v2 ? selectedActivity.fm_swms_actions_v2.sort((a, b) => a.action_order - b.action_order).map(action => ({
          action_name: action.action_name,
          id: action.action_id,
          hazards: action.fm_swms_activities_hazards_v2 ? action.fm_swms_activities_hazards_v2.sort((a, b) => a.activity_hazard_order - b.activity_hazard_order).map(hazard => ({
            id: hazard.hazard_id,
            hazard_name: hazard.hazard_name,
            consequences: hazard.fm_swms_hazards_consequences_v2 ? hazard.fm_swms_hazards_consequences_v2.sort((a, b) => a.consequences_order - b.consequences_order).map(consequence => ({
              id: consequence.consequence_id,
              consequence_name: consequence.consequences_name,
            })) : [{}],
          })) : [{ hazard_name: "", consequences: [{}] }],
          control_measures: action.control_measures ? action.control_measures : "",
          after_consequence: action.after_consequence ? ({
            value: action.after_consequence.id,
            label: action.after_consequence.name,
            cons_value: action.after_consequence.value
          }) : null,
          before_consequence: action.before_consequence ? ({
            value: action.before_consequence.id,
            label: action.before_consequence.name,
            cons_value: action.before_consequence.value
          }) : null,
          after_likelihood: action.after_likelihood ? ({
            value: action.after_likelihood.id,
            label: action.after_likelihood.name,
            like_value: action.after_likelihood.value
          }) : null,
          before_likelihood: action.before_likelihood ? ({
            value: action.before_likelihood.id,
            label: action.before_likelihood.name,
            like_value: action.before_likelihood.value
          }) : null,
          initialRisk: action.initial_risk ? action.initial_risk : action.before_consequence && action.before_consequence.value && action.before_likelihood && action.before_likelihood.value ? action.before_consequence.value * action.before_likelihood.value : null,
          residualRisk: action.residual_risk ? action.residual_risk : action.after_consequence && action.after_consequence.value && action.after_likelihood && action.after_likelihood.value ? action.after_consequence.value * action.after_likelihood.value : null,
          persons_in_control: action.persons_in_control ? action.persons_in_control : null,
        })) : [{ action_name: "", hazards: [{ hazard_name: "", consequences: [{}] }] }],
        created_at: selectedActivity.created_at,
        created_by: selectedActivity.created_by,
        created_by_name: selectedActivity.created_by_name,
        modified_at: selectedActivity.modified_at,
        modified_by: selectedActivity.modified_by,
        modified_by_name: selectedActivity.modified_by_name,
        edited: selectedActivity.edited,
        owner: selectedActivity.owner,
        owner_name: selectedActivity.owner_name,
        approver: selectedActivity.approver,
        approver_name: selectedActivity.approver_name,
        approved_at: selectedActivity.approved_at,
        next_review_date: selectedActivity.next_review_date,
        status: selectedActivity.status,
        submitted_at: selectedActivity.submitted_at,
        org_id: selectedActivity.org_id,
        toggled_active: selectedActivity.toggled_active,
        active: selectedActivity.active,
        history: selectedActivity.history ? selectedActivity.history : [],
      }
      setInitialValues(activityValues);
      dispatch(change("swms_jsa_2activity_details_form", "_v", Date.now()));
    } else {
      setInitialValues({ actions: [{ action_name: "", hazards: [{ hazard_name: "", consequences: [{}] }] }] });
    }
  }, [selectedActivity])

  const fetchAllData = () => {
    setLoadingData(true);
    controller.current = new AbortController();
    const requests = [
      dispatch(getLikelihoods({ signal: controller.current.signal }, { active: 1 })),
      dispatch(getConsequences({ signal: controller.current.signal }, { active: 1 })),
      dispatch(getCategoryFilters({ signal: controller.current.signal, params: { active: 1 } }))
    ];
    Promise.all(requests).then((res) => {
      setLikelihoods(res[0].map(x => ({ label: x.name, value: x.id, like_value: x.value })));
      setConsequences(res[1].map(x => ({ label: x.name, value: x.id, cons_value: x.value })));
      setCategories(res[2].categories.filter(x => x.active === 1));
      setLoadingData(false);
    }).catch((err) => {
      if (err) {
        console.log(err);
        setLoadingData(false);
        notification.error({
          message: Strings.error_title,
          description: "There was an error while fetching Activity Data.",
          className: "ant-error"
        })
      }
    })
  }

  useEffect(() => {
    fetchAllData();
  }, []);

  const isNewActivity = useMemo(() => {
    return !selectedActivity || (selectedActivity && !selectedActivity.activity_id) ? true : false;
  }, [selectedActivity]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <AddEditJSA2ActivityForm
            initialValues={initialValues}
            loadingData={loadingData}
            categories={categories}
            isNewActivity={isNewActivity}
            onActivitySubmit={onActivitySubmit}
            goBack={() => { onClose() }}
            likelihoods={likelihoods}
            consequences={consequences}
          />
        </div>
      </div>
      {!isNewActivity ? (
        <div className="row">
          <div className="col-12 mb-3" style={{ fontFamily: "'Poppins', sans-serif", color: "#212529" }}>
            <div className="h6 mt-3" style={{ fontWeight: 600 }}>Activity History</div>
            <div>
              {selectedActivity.history && selectedActivity.history.length ? selectedActivity.history.map((v, i) => (
                <HistoryItem key={i} history={v} />
              )) : (
                <div className="py-2 border-bottom">
                  <div style={{ color: "#f44336" }}>No History Available.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}


const HistoryItem = ({ history }) => {
  return (
    <div className="swms-activity-history-row py-2 border-bottom">
      <div className="d-flex" style={{ gap: 12 }}>
        <div className="swms-activity-history-left" style={{ flex: 1, minWidth: 0 }}>
          <div className="swms-activity-history-clamp2" title={`${formatAction(history.status)} — ${history.description || ""}`}>
            <div className="swms-activity-history-action">{formatAction(history.status)}</div>
            <div className="swms-activity-history-desc">{history.description ? history.description : "—"}</div>
          </div>
        </div>
        <div className="swms-activity-history-right text-right" style={{ flex: "none" }}>
          <div className="swms-activity-history-person" title={history.performed_by_name}>
            {history.created_by_name}
          </div>
          <div className="swms-activity-history-time" title={history.created_at}>
            {dateFormatter(history.created_at)}
          </div>
        </div>
      </div>
    </div>
  )
}